import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";

import { IamAuthContext } from "@megaron/auth-react";
import { SignalDoc, signalDocRef } from "@megaron/docs-contracts";
import { useClientManager } from "@megaron/react-clients";

import { isSignalUnread } from "./signalUnread";

type SignalsContextValue = {
  signals: SignalDoc[];
  unreadSignals: SignalDoc[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
  markRead: (docIds: SignalDoc["docId"][]) => void;
};

const SignalsContext = createContext<SignalsContextValue>({
  signals: [],
  unreadSignals: [],
  isLoading: false,
  isError: false,
  refetch: () => Promise.resolve(),
  markRead: () => undefined,
});

const signalReadKey = (signal: SignalDoc) => `${signal.docId}:${signal.createdAt.getTime()}`;

const signalsQueryKey = (userKey: string | undefined, limit: number) =>
  ["docs", "searchSignals", userKey, { limit }] as const;

export const SignalsProvider: React.FC<{
  children: React.ReactNode;
  isAuthLoaded: boolean;
}> = ({ children, isAuthLoaded }) => {
  const auth = useContext(IamAuthContext);
  const userKey = auth.iamUser?.id;
  const signalsLimit = 1000;
  const sentReadKeys = useRef(new Set<string>());

  useEffect(() => {
    sentReadKeys.current.clear();
  }, [userKey]);

  const query = useClientManager("docs")
    .searchSignals()
    .useQuery(
      { limit: signalsLimit },
      {
        enabled: isAuthLoaded && Boolean(userKey),
        queryKey: [...signalsQueryKey(userKey, signalsLimit)],
        refetchInterval: isAuthLoaded && Boolean(userKey) ? 5_000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        refetchOnReconnect: true,
      },
    );

  const setReadMutation = useClientManager("docs")
    .setSignalRead()
    .useMutation({
      onSuccess: () => {
        void query.refetch();
      },
    });

  const signals = useMemo((): SignalDoc[] => query.data?.items ?? [], [query.data?.items]);

  const unreadSignals = useMemo(() => signals.filter(isSignalUnread), [signals]);

  const signalsByDocId = useMemo(() => new Map(signals.map((signal) => [signal.docId, signal])), [signals]);

  const markRead = useCallback(
    (docIds: SignalDoc["docId"][]) => {
      const pending = docIds
        .flatMap((docId) => signalsByDocId.get(docId) ?? [])
        .filter((signal) => isSignalUnread(signal) && !sentReadKeys.current.has(signalReadKey(signal)));

      if (pending.length === 0) return;

      pending.forEach((signal) => sentReadKeys.current.add(signalReadKey(signal)));
      setReadMutation.mutate({ signals: pending.map(signalDocRef), isRead: true });
    },
    [signalsByDocId, setReadMutation],
  );

  return (
    <SignalsContext.Provider
      value={{
        signals,
        unreadSignals,
        isLoading: query.isLoading,
        isError: query.isError,
        refetch: query.refetch,
        markRead,
      }}
    >
      {children}
    </SignalsContext.Provider>
  );
};

export const useSignals = (): SignalsContextValue => useContext(SignalsContext);
